import { useCallback } from "react";
import { LogOut } from "lucide-react";
import Button from "@mui/material/Button";
import { updateSession } from "@/api/auth";
import { useAccountSessions } from "@/hooks/useAccountSessions";
import { cn } from "@/lib/utils";
import { formatSessionDate } from "@/lib/sessionFormatting";
import { DataTableError } from "@/components/shared/DataTable";
import { usePendingAction } from "@/hooks/usePendingAction";
import { toast } from "sonner";

interface SessionsListProps {
  className?: string;
}

export function SessionsList({ className }: SessionsListProps) {
  const { data: sessions, isLoading, isError, refetch } = useAccountSessions();
  const { isPending, run } = usePendingAction();

  const handleLogout = useCallback(
    async (sessionId?: string) => {
      if (!sessionId) return;
      await run(async () => {
        try {
          await updateSession(sessionId);
        } catch {
          toast.error(
            "Could not log out this session. Check the refreshed session list before trying again.",
          );
        }
        await refetch();
      });
    },
    [refetch, run],
  );

  if (isLoading) {
    return (
      <div className={cn("space-y-2", className)}>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-9 animate-pulse rounded-lg bg-muted/40" />
        ))}
      </div>
    );
  }

  if (isError)
    return (
      <div className={className}>
        <DataTableError
          message="Could not load active sessions. Try loading them again."
          onRetry={refetch}
        />
      </div>
    );

  if (!sessions || sessions.length === 0) return null;

  return (
    <div className={cn(className)}>
      <p className="text-sm font-semibold">
        Active Sessions ({sessions.length})
      </p>
      <div className="mt-3 space-y-3">
        {sessions.map((s, i) => (
          <div key={i} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {formatSessionDate(s.loggedInAt)}
              </p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {[s.deviceType, s.browserType].filter(Boolean).join(" - ") ||
                  "Unknown device"}
              </p>
            </div>
            {s.currentSession ? (
              <span className="shrink-0 text-xs font-medium text-primary">
                Current session
              </span>
            ) : (
              <Button
                variant="text"
                size="small"
                disabled={isPending}
                sx={{
                  flexShrink: 0,
                  gap: "0.375rem",
                  color: "var(--muted-foreground)",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "var(--destructive)",
                  },
                }}
                onClick={() => handleLogout(s.sessionIdentifier)}
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
